import { Component } from '@angular/core';
import { ProfileService } from 'src/app/_core/services/profile.service';
import { AreaType } from 'src/app/_core/interfaces/IAreaType';
import { ElementType } from 'src/app/_core/models/ToolboxModel';
import { IBomInfo, IElement } from 'src/app/_core/interfaces/IElement';
import { BarProfile } from 'src/app/_core/models/profiles/bar.model';
import { ProjectService } from 'src/app/_core/services/project.service';

export class ProjectTreeItem {
  id: string;
  parentId?: string | null;
  name: string;
  height?: number | null;
  depth?: number | null;
  width?: number | null;
  elementType: ElementType;
  selected?: boolean;
}

const areasTransl = new Map<AreaType, string>([
	[AreaType.Roof, $localize`:BOM|'Roof' label:Roof`],
	[AreaType.Left, $localize`:BOM|'Left side' label:Left`],
	[AreaType.Right, $localize`:BOM|'Right side' label:Right`],
	[AreaType.Front, $localize`:BOM|'Front side' label:Front side`],
	[AreaType.Rear, $localize`:BOM|'Rear' label:Back`],
]);

enum BomElementsName {
	glasses = 'glasses',
	bars = 'bars',
	columns = 'columns',
	rears = 'rears',
	fronts = 'fronts',
	marquises = 'marquises',
	ledSpots = 'ledSpots',
	ledStripes = 'ledStripes',
	roofWindows = 'roofWindows',
	roofGutters = 'roofGutters',
	wallGlasses = 'wallGlasses',
	diamondGlasses = 'diamondGlasses',
	extras = 'extras',
	ventilators = 'ventilators',
	wallGutters = 'wallGutters',
	doors = 'doors',
	walls = 'walls',
	comments = 'comments',
}

const elementsTransl = new Map<BomElementsName, string>([
	[BomElementsName.glasses, $localize`:BOM|'Glasses' label:Glasses`],
	[BomElementsName.bars, $localize`:BOM|'Bars' label:Bars`],
	[BomElementsName.columns, $localize`:BOM|'Columns' label:Columns`],
	[BomElementsName.rears, $localize`:BOM|'Rears' label:Rears`],
	[BomElementsName.fronts, $localize`:BOM|'Fronts' label:Fronts`],
	[BomElementsName.marquises, $localize`:BOM|'Marquises' label:Marquises`],
	[BomElementsName.ledSpots, $localize`:BOM|'LED spots' label:LED spots`],
	[BomElementsName.ledStripes, $localize`:BOM|'LED stripes' label:LED stripes`],
	[BomElementsName.wallGlasses, $localize`:BOM|Wall glasses:Wall glasses`],
	[BomElementsName.diamondGlasses, $localize`:BOM|Diamond glasses:Diamond glasses`],
	// [BomElementsName.extras, $localize`:BOM|'Extras' label:Extras`],
	[BomElementsName.ventilators, $localize`:BOM|'Ventilators' label:Ventilators`],
	[BomElementsName.wallGutters, $localize`:BOM|'Wall gutters' label:Wall gutters`],
	[BomElementsName.roofWindows, $localize`:BOM|'Roof windows' label:Roof windows`],
	[BomElementsName.roofGutters, $localize`:BOM|'Roof gutters' label:Roof gutters`],
	[BomElementsName.doors, $localize`:BOM|Door:Door`],
	[BomElementsName.walls, $localize`:BOM|Wall:Wall`],
]);

const bomElementsMap = new Map<BomElementsName, ElementType[]>([
  [BomElementsName.glasses, [ElementType.Glass]],
  [BomElementsName.bars, [ElementType.Bar]],
  [BomElementsName.columns, [ElementType.Column]],
  [BomElementsName.rears, [ElementType.WallProfile]],
  [BomElementsName.fronts, [ElementType.Front]],
  [BomElementsName.marquises, [ElementType.MarquiseTopUp, ElementType.MarquiseTopBottom, ElementType.MarquiseVertical]],
  [BomElementsName.ledSpots, [ElementType.Led]],
  [BomElementsName.ledStripes, [ElementType.LedStripe]],
  [BomElementsName.wallGlasses, [ElementType.GlassWall]],
  [BomElementsName.diamondGlasses, [ElementType.GlassWallDiamond]],
  [BomElementsName.wallGutters, [ElementType.WallGutter]],
  [BomElementsName.roofWindows, [ElementType.RoofWindow]],
  [BomElementsName.roofGutters, [ElementType.RoofGutter]],
  [BomElementsName.doors, [ElementType.Door]],
  [BomElementsName.walls, [ElementType.Wall]]
]);

const elementsOnAreasMap = new Map<AreaType, ElementType[]>([
  [AreaType.Roof, [
    ElementType.Glass,
    ElementType.Bar,
    ElementType.SideFinish,
    ElementType.Column,
    ElementType.Led,
    ElementType.LedStripe,
    ElementType.RoofWindow,
    ElementType.RoofGutter,
    ElementType.WallGutter,
    ElementType.Front,
    ElementType.WallProfile,
    ElementType.MarquiseTopBottom,
    ElementType.MarquiseTopUp,
  ]],
  [AreaType.Left, [
    ElementType.GlassWallDiamond,
    ElementType.GlassWall,
    ElementType.Door,
    ElementType.Wall,
    ElementType.MarquiseVertical
  ]],
  [AreaType.Right, [
    ElementType.GlassWallDiamond,
    ElementType.GlassWall,
    ElementType.Door,
    ElementType.Wall,
    ElementType.MarquiseVertical
  ]],
  [AreaType.Front, [
    ElementType.GlassWall,
    ElementType.Door,
    ElementType.Wall,
    ElementType.MarquiseVertical
  ]],
  [AreaType.Rear, [
    ElementType.GlassWall,
    ElementType.Door,
    ElementType.Wall,
  ]],
]);

@Component({
  selector: 'app-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.css']
})
export class OfferComponent {
  projectItems: ProjectTreeItem[] = [];
  itemsSelected: any[] = [];
  changedItems: any[] = [];

  constructor (private profileService: ProfileService, private projectService: ProjectService) {}

  ngOnInit() {
    this.populateTree();
  }

  private populateTree() {
      this.projectItems = [];
      this.itemsSelected = [];
  
      Object.values(AreaType).forEach(areaKey => {
        const tr = areasTransl.get(areaKey);
        if (!tr) {
          return;
        }
        this.projectItems.push({ id: areaKey, parentId: null, name: tr, elementType: ElementType.None });
  
        Object.values(BomElementsName).forEach(bomKey => {
          const optionKey = areaKey + "_" + bomKey;
          const name = elementsTransl.get(bomKey);
          if (!name) {
            return;
          }
          const elTypes = bomElementsMap.get(bomKey);
          if (!elTypes || elTypes.length == 0) {
            return;
          }

          elTypes.forEach(elType => {

            if (elementsOnAreasMap.get(areaKey).indexOf(elType) === -1) {
              return;
            }
            const elItems: Set<ProjectTreeItem> = new Set<ProjectTreeItem>();
            this.profileService.roofElements[elType]?.forEach((el: IElement & IBomInfo) => {
              el.inBOM = true;
              const r = el.getBomInfo();
              elItems.add({ id: el.id, parentId: optionKey, name: el.optionName, height: r?.height, width: r?.width, depth: r?.depth, elementType: elType, selected: el.inOffer });
            });
            if (bomKey == BomElementsName.bars) {
              this.profileService.roofElements[ElementType.SideFinish]?.forEach((el: IElement & IBomInfo) => {
                el.inBOM = true;
                const r = el.getBomInfo();
                elItems.add({ id: el.id, parentId: optionKey, name: el.optionName, height: r?.height, width: r?.width, depth: r?.depth, elementType: elType, selected: el.inOffer });
              });
            }
            
            this.profileService.getVerticals(areaKey, elType).forEach((el: IElement & IBomInfo) => {
              el.inBOM = true;
              const v = el.getBomInfo();
              elItems.add({ id: el.id, parentId: optionKey, name: el.optionName, height: v?.height, width: v?.width, depth: v?.depth, elementType: elType, selected: el.inOffer });
            });
            if (elItems.size > 0) {
              if (!this.projectItems.find(i => i.id == optionKey)) {
                this.projectItems.push({ id: optionKey, parentId: areaKey, name: elementsTransl.get(bomKey), elementType: ElementType.None, selected: false });
              }
              this.projectItems.push(...elItems);
            }

        });

        });
      });
  
      this.projectItems.filter(i => i.parentId != null).forEach(item => {
        this.selectParents(item, true);
      });
      this.projectItems.filter(i => i.parentId == null).forEach(item => {
        this.selectParents(item, false);
      });
    }
  
    private selectParents(item: ProjectTreeItem, hasParent: boolean) {
      const children = this.projectItems.filter(i => i.parentId === item.id);
      const selectedChildren = children.filter(i => i.selected == true);
  
      if (!hasParent && selectedChildren.length == 0) {
        item.selected = false;
      } else if (children.length > 0) {
        if (selectedChildren.length == children.length) {
          item.selected = true;
        } else if (selectedChildren.length > 0) {
          item.selected = null;
        } else if (selectedChildren.length == 0) {
          item.selected = false;
        }
      }
  
      if (item.parentId) {
        const parent = this.projectItems.find(i => i.id == item.parentId);
        this.selectParents(parent, parent.parentId != null);
      }
    }

  onCheckboxChange(event: any, element: ProjectTreeItem) {
		const checked = (event.target as HTMLInputElement).checked;
		if (element) {
			this.changedItems.push(element);
			element.selected = checked;

			const children = this.projectItems.filter(i => i.parentId == element.id);
			this.changedItems.push(...children);

			children.forEach(c =>{
				c.selected = checked;
				const grandchildren = this.projectItems.filter(i => i.parentId == c.id);
				this.changedItems.push(...grandchildren);

				grandchildren.forEach(gc => {
					gc.selected = checked;
				});
			})

			if (element.parentId) {
				const parent = this.projectItems.find(i => i.id == element.parentId);
				this.selectParents(parent, parent.parentId != null);
			}
		}
	}

	changeElements(mode: string) {
    this.changedItems.forEach(item => {
      const selected = item.selected
      const itemId = item.id;
      const itemType = item.elementType;
      var foundEl = this.profileService.roofElements[itemType].find(i => i.id === itemId);
      if (!foundEl && itemType == ElementType.Bar) {
        foundEl = this.profileService.roofElements[ElementType.SideFinish].find(i => i.id === itemId);
      }
      if (foundEl) {
        foundEl[mode] = selected;
      } else {
        var foundEl2 = this.profileService.getVerticals(AreaType.None, itemType).find(i => i.id === itemId);
        if (foundEl2) {
          foundEl2[mode] = selected;
        }
      }
	  });
    this.changedItems = [];
  }
}
